import { useMemo, useState } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { AuthGate } from './components/AuthGate'
import { Header } from './components/Header'
import { Board } from './components/Board'
import { CardDetail } from './components/CardDetail'
import { DoneDrawer } from './components/DoneDrawer'
import { useBoard } from './lib/useBoard'
import { useStickyState } from './lib/useStickyState'
import { ASSIGNEES, type Assignee } from './lib/types'

const isAssignee = (v: unknown): v is Assignee => ASSIGNEES.includes(v as Assignee)
const isFilter = (v: unknown): v is Assignee | 'all' => v === 'all' || isAssignee(v)

function LanternBoard() {
  const board = useBoard()
  const [filter, setFilter] = useStickyState<Assignee | 'all'>('lantern.filter', 'all', isFilter)
  const [lastAssignee, setLastAssignee] = useStickyState<Assignee>(
    'lantern.lastAssignee',
    'john',
    isAssignee,
  )
  const [openCardId, setOpenCardId] = useState<string | null>(null)
  const [doneOpen, setDoneOpen] = useState(false)

  // Read the open card back out of board state so optimistic edits show through.
  const openCard = useMemo(
    () => board.allCards.find((c) => c.id === openCardId) ?? null,
    [board.allCards, openCardId],
  )

  const doneCards = useMemo(
    () => (filter === 'all' ? board.done : board.done.filter((c) => c.assignee === filter)),
    [board.done, filter],
  )

  return (
    <div className="min-h-dvh">
      <Header
        filter={filter}
        onFilterChange={setFilter}
        doneCount={doneCards.length}
        onOpenDone={() => setDoneOpen(true)}
      />

      {board.error && (
        <div className="mx-auto mt-3 flex max-w-7xl items-center gap-2 rounded-lg bg-flame/10 px-4 py-2.5
                        text-sm text-flame sm:px-6"
        >
          <AlertTriangle className="size-4 shrink-0" />
          <span className="flex-1">{board.error}</span>
          <button
            type="button"
            onClick={() => void board.reload()}
            className="font-semibold underline underline-offset-2"
          >
            Reload
          </button>
          <button type="button" onClick={board.dismissError} aria-label="Dismiss">
            <X className="size-4" />
          </button>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {board.loading ? (
          <div className="grid place-items-center py-24">
            <Loader2 className="size-6 animate-spin text-teal-400" aria-label="Loading board" />
          </div>
        ) : (
          <Board
            columns={board.columns}
            filter={filter}
            defaultAssignee={lastAssignee}
            onAdd={(input) => {
              setLastAssignee(input.assignee)
              void board.addCard(input)
            }}
            onOpen={(card) => setOpenCardId(card.id)}
            onToggleDone={(card) => void board.markDone(card)}
            onMove={(id, columns, status, index) => void board.persistMove(id, columns, status, index)}
          />
        )}
      </main>

      {openCard && (
        <CardDetail
          card={openCard}
          onClose={() => setOpenCardId(null)}
          onSave={(id, patch) => {
            if (patch.assignee) setLastAssignee(patch.assignee)
            void board.patchCard(id, patch)
          }}
          onToggleDone={(card) => void board.markDone(card)}
          onDelete={(card) => void board.removeCard(card)}
        />
      )}

      {doneOpen && (
        <DoneDrawer
          cards={doneCards}
          filtered={filter !== 'all'}
          onClose={() => setDoneOpen(false)}
          onRestore={(card) => void board.restore(card)}
          onDelete={(card) => void board.removeCard(card)}
        />
      )}
    </div>
  )
}

export default function App() {
  return (
    <AuthGate>
      <LanternBoard />
    </AuthGate>
  )
}
